import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../../../utils';
import { withTheme } from 'styled-components';
import Badge from '../../../Badge';
import Icon from '../../../Icon';
import Translate, { translate } from '../../../Translate';
import { FilterSearchWithRef } from './FilterSearch';
import FilterValueComponent from './FilterValue';
import ClickLayer from '../../../ClickLayer';
import {
    FilterItemProps,
    FilterValue,
} from '../../../../models/components/Filter';
import { action, computed, observable } from 'mobx';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    List,
    CellMeasurer,
    CellMeasurerCache,
    AutoSizer,
} from 'react-virtualized';
import InlineDisclaimers from '../../../InlineDisclaimers';
import { SuperscriptScope } from '../../../../stores';
import { Popper } from '../../../../utils/Popper';
import { createPortal } from 'react-dom';
import { Modifier, OptionsGeneric } from '@popperjs/core';
import { DropdownEventHandler } from '../../../Dropdown/DropdownEventHandler';
import { sortBy } from '@kurtosys/ksys-app-template/dist/utils/collection';
import FilterItemDropdownButton from './FilterItemDropdownButton';
import { KEYBOARD_KEYS } from 'src/constants';
import FilterDropdown from '../StyledComponents/FilterDropdown';
import FilterDropdownHeader from '../StyledComponents/FilterDropdownHeader';
import FilterDropdownInnerWrapper from '../StyledComponents/FilterDropdownInnerWrapper';
import FilterDropdownLabelWrapper from '../StyledComponents/FilterDropdownLabelWrapper';
import FilterDropdownRow from '../StyledComponents/FilterDropdownRow';
import FilterDropdownText from '../StyledComponents/FilterDropdownText';
import FilterDropdownWrapper from '../StyledComponents/FilterDropdownWrapper';
import FilterList from '../StyledComponents/FilterList';
import FilterListAutosizerWrapper from '../StyledComponents/FilterListAutosizerWrapper';
import SelectedFilterInfo from '../StyledComponents/SelectedFilterInfo';
import ClearIndividualFiltersButton from '../StyledComponents/ClearIndividualFiltersButton';
import Divider from '../StyledComponents/Divider';
import VisuallyHiddenWrapper from '../../../VisuallyHiddenWrapper';

export interface RowItem {
    index: number;
    key: any;
    style: any;
    parent: any;
}

const rightAlignStyleProps = {
    order: 1,
    margin: '0 0 0 auto',
    zIndex: 1010,
};

@inject('dataStore')
@inject('translationStore')
@inject('filterStore')
@inject('accessibilityStore')
@observer
class FilterItemDropdown extends Popper<FilterItemProps> {
    @observable updated = 1; // Setting this forces the autosizer to re-render when the component props change
    @observable cache: CellMeasurerCache;
    @observable.ref showDropdown: boolean = false;
    @observable.ref searchTerm = '';
    @observable.ref searchResults: Array<FilterValue> = [];

    filterSearchInputRef = null;

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = this
        .props?.popperOptions
        ? this.props.popperOptions
        : {
            placement: 'bottom-start',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 1],
                    },
                },
                {
                    name: 'flip',
                    enabled: false,
                },
                {
                    ...this.sameWidth,
                },
            ],
        };

    @observable dropdownEventHandler: DropdownEventHandler;

    constructor(props) {
        super(props);

        const { filter, enableSearch } = this.props as FilterItemProps;

        this.cache = new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: props.defaultRowHeight || 100,
        });

        this.filterSearchInputRef = React.createRef();

        this.searchResults = [];
        this.dropdownEventHandler = new DropdownEventHandler({
            items: this.dropdownItems,
            pageKeyTraverseSize: 3,
            isMultiSelect: filter.ui !== 'dropdown',
            hasSearch: enableSearch,
            filterSearchInputRef: this.filterSearchInputRef,
            onItemSelect: this.handleFilterKeyPress,
            resetFn: this.resetDropdownItemsAndEvents,
        });
    }

    @computed
    get popperEnabled(): boolean {
        return this.dropdownEventHandler.isOpen;
    }

    @computed
    get searchResultCount(): number | null {
        if (this.searchTerm !== '') {
            return this.dropdownItems.length;
        }
        return null;
    }

    @computed
    get dropdownItems(): Array<FilterValue> {
        if (this.searchTerm !== '') {
            return this.searchResults;
        }
        
        const items = this.getAllDropDownItems();
        return items;
    }

    @computed
    get dropdownItemsCount(): number {
        const { filter } = this.props;
        return filter.values.length;
    }

    @computed
    get showClear() {
        return this.searchTerm.length > 0;
    }

    @computed
    get filteredValues() {
        return this.props && this.props.filteredValues;
    }
    
    @computed
    get selectedValues() {
        return this.props && this.props.selectedValues;
    }

    @computed
    get filter() {
        return this.props && this.props.filter;
    }

    @computed
    get mustVirtualize() {
        const { filter } = this.props;
        return filter.dropdownOptions && filter.dropdownOptions.virtualize;
    }

    @computed
    get autosizeFn() {
        const overscanRowCount = 10;
        return ({ width, height }: { width: any; height: any }) => {
            return (
                <List
                    updated={this.updated++}
                    width={width}
                    height={height}
                    rowHeight={this.cache.rowHeight}
                    rowRenderer={this.renderRow}
                    rowCount={this.dropdownItems.length}
                    overscanRowCount={overscanRowCount}
                />
            );
        };
    }

    @computed
    get filterDropdownHiddenStyle(): React.CSSProperties {
        if (!this.showDropdown) {
            return {
                visibility: 'hidden',
                pointerEvents: 'none',
            };
        }
        return {};
    }

    @computed
    get buttonKeyboardProps() {
        return {
            onKeyDown: this.dropdownEventHandler.keyboardEvents.handleKeyboardEventsFor(
                'down'
            ),
            onkeyup: this.dropdownEventHandler.keyboardEvents.handleKeyboardEventsFor(
                'up'
            ),
        };
    }

    @computed
    get itemKeyboardProps() {
        const { keyboardEvents } = this.dropdownEventHandler;

        return {
            onKeyDown: keyboardEvents.handleKeyboardEventsFor('down'),
            onKeyUp: keyboardEvents.handleKeyboardEventsFor('up'),
        };
    }

     @computed
    get sortedFilteredValues() {
        return this.props && this.props.filteredValues;
    }

    getAllDropDownItems = (): Array<FilterValue> => {
        const { selectedValues, filteredValues } = this
            .props as FilterItemProps;
            
        // Helper function to determine if a filter value is active/enabled
        const isFilterValueActive = (filterValue: FilterValue): boolean => {
            const isActive = filterValue.isActive;
            return isActive;
        };
        
        // Helper function to apply sorting with active items first
        const sortFilters = (items: Array<FilterValue>, sortKey: 'label' | 'value'): Array<FilterValue> => {
            // First sort by the specified key
            const sortedByKey = sortBy(items, item => item[sortKey]);
            
            if(this.filter.sortEnabledFirst) {
                // Then partition into active and inactive groups while preserving the key sort
                const activeItems = sortedByKey.filter(item => isFilterValueActive(item));
                const inactiveItems = sortedByKey.filter(item => !isFilterValueActive(item));
            
                // Return active items first, then inactive items
                return [...activeItems, ...inactiveItems];
            }
            
            return sortedByKey;
        };
        
        let result: Array<FilterValue>;
        
        if (this.filter.sortActiveFirst && selectedValues.length > 0) {
            result = [
                ...sortFilters(selectedValues, 'label'),
                ...sortFilters(filteredValues, 'label'),
            ];
        } else if (this.filter.sortByNameAlias) {
            result = sortFilters(
                [...selectedValues, ...filteredValues],
                'label'
            );
        }
        else {
            result = sortFilters(
                [...selectedValues, ...filteredValues],
                'value'
            );
        }
        
        return result;
    };

    @action
    clearSearch = () => {
        this.searchTerm = '';
    };

    @action
    searchDropdownItems = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { filterStore } = this.props;
        const { property, filterSearchKey } = this.filter;
        const searchTerm = event.target.value;
        
        if (searchTerm !== '') {
            const dropdownItems = this.getAllDropDownItems().reduce(
                (items: Array<FilterValue>, filterValue: FilterValue) => {
                    if (
                        filterStore.isChecked(
                            property,
                            filterValue.value,
                            filterSearchKey
                        )
                    ) {
                        items.push(filterValue);
                    } else if (
                        filterValue.label
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                    ) {
                        items.push(filterValue);
                    }
                    return items;
                },
                []
            );

            this.searchResults = dropdownItems;
            this.dropdownEventHandler.updateItems(dropdownItems);
        } else {
            this.resetDropdownItemsAndEvents();
        }

        this.searchTerm = searchTerm;
    };

    @action
    resetDropdownItemsAndEvents = () => {
        this.clearSearch();
        this.searchResults = [];
        this.dropdownEventHandler.updateItems(this.dropdownItems);
    };

    @action
    setFilterDropdownElement = (element: HTMLElement) => {
        this.setPopupNodeRef(element);
        // Show the visually hidden Dropdown after Popper initialization, this ensures correct positioning
        if (element) {
            this.showDropdown = true;
            return;
        }
        this.showDropdown = false;
    };

    handleToggleFilter = (
        event:
            | React.KeyboardEvent<HTMLLIElement>
            | React.ChangeEvent<HTMLLIElement>,
        filterValue: FilterValue
    ) => {
        const { filterStore, filter, selectedValues } = this.props;
        const { toggleOpen } = this.dropdownEventHandler;
        const {
            property,
            filterSearchKey,
            ui,
            allowInactiveSelection,
        } = filter;

        const checked = selectedValues?.some(
            value => value.value === filterValue.value
        );

        const active = filterStore?.isActive(property, filterValue.value);
        const disabled = allowInactiveSelection === false && active === false;

        if (!disabled) {
            filterStore.toggleFilter(
                property,
                filterValue.value,
                filterSearchKey,
                checked,
                filter
            );
            if (ui === 'dropdown') {
                toggleOpen(
                    (event as unknown) as React.ChangeEvent<HTMLElement>
                );
            }
        }
    };

    // This is used for the divider position
    lastCheckedItem = (): FilterValue | null => {
        const { filterStore, selectedValues } = this.props;
        const { property, filterSearchKey, sortActiveFirst } = this.filter;

        if (sortActiveFirst && selectedValues.length > 0) {
            // Here we check the checked items vs unchecked
            // To ensure that the divider stays in the correct place while
            // Selecting more items in the dropdown
            const lastSelectedItem = this.dropdownItems.find(
                (filterValue: FilterValue, idx: number) => {
                    const currentItem = filterStore.isChecked(
                        property,
                        filterValue.value,
                        filterSearchKey
                    );

                    const nextItem = filterStore.isChecked(
                        property,
                        this.dropdownItems[idx + 1]?.value,
                        filterSearchKey
                    );

                    let previousItem = false;
                    if (idx > 0) {
                        previousItem = filterStore.isChecked(
                            property,
                            this.dropdownItems[idx - 1]?.value,
                            filterSearchKey
                        );
                        if (!previousItem) {
                            return false;
                        }
                    }

                    if (
                        currentItem &&
                        previousItem &&
                        previousItem === currentItem &&
                        currentItem !== nextItem
                    ) {
                        return true;
                    }

                    if (
                        selectedValues.length === 1 &&
                        idx === 0 &&
                        currentItem
                    ) {
                        return true;
                    }
                }
            );
            return lastSelectedItem;
        }

        return null;
    };

    renderFilterValue = (filterValue: FilterValue, index: number = 0) => {
        const { filter, theme, selectedValues, filterStore } = this.props;

        const {
            property,
            ui,
            sortActiveFirst,
            allowInactiveSelection = true,
        } = filter;

        const checked = selectedValues?.some(
            value => value.value === filterValue.value
        );

        const active = filterStore?.isActive(property, filterValue.value);
        const disabled = allowInactiveSelection === false && active === false;

        const onChange = (event: React.ChangeEvent<HTMLLIElement>) => {
            this.handleToggleFilter(event, filterValue);
        };

        const showDivider =
            sortActiveFirst &&
            this.lastCheckedItem()?.value === filterValue.value;

        return (
            <FilterValueComponent
                // This ensures that the screen reader reads out the state of the item correctly
                // -1 = not selected
                // 0  = selected
                tabIndex={checked ? 0 : -1}
                role="option"
                aria-selected={checked}
                aria-disabled={disabled}
                key={`${filterValue.label}`}
                label={filterValue.label}
                ui={ui}
                value={filterValue.value}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                onFocus={() => {
                    // This ensures that the correct item has focus when tabbing onto an element from the search input
                    if (this.dropdownEventHandler.focusedIndex !== index) {
                        this.dropdownEventHandler.focusedIndex = index;
                    }
                }}
                active={active}
                filterProperty={property}
                index={index}
                isOpen={this.dropdownEventHandler.isOpen}
                isFocused={index === this.dropdownEventHandler.focusedIndex}
                instanceListener={this.dropdownEventHandler.childReferences.getChildIndexListener(
                    index
                )}
                {...this.itemKeyboardProps}
                {...theme.FilterDropdownFilterListItem}
            >
                {showDivider && <Divider />}
            </FilterValueComponent>
        );
    };

    renderRow = ({ index, key, style, parent }: RowItem) => {
        const filteredValue = this.dropdownItems[index];
        return (
            <CellMeasurer
                key={key}
                cache={this.cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                <FilterDropdownRow key={key} style={style}>
                    {this.renderFilterValue(filteredValue)}
                </FilterDropdownRow>
            </CellMeasurer>
        );
    };

    handleFilterKeyPress = (
        event: React.KeyboardEvent<HTMLLIElement>,
        filterValue: FilterValue,
        _: number
    ) => {
        this.handleToggleFilter(event, filterValue);
    };

    handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { ESCAPE, UP_ARROW, DOWN_ARROW } = KEYBOARD_KEYS;

        if (event.key === ESCAPE) {
            // Makes sure that the Escape key works when the input is focussed
            this.dropdownEventHandler.closeDropdown();
            this.clearSearch();
        } else if (event.key === UP_ARROW) {
            this.dropdownEventHandler.focusedIndex =
                this.dropdownItems.length - 1;
        } else if (event.key === DOWN_ARROW) {
            this.dropdownEventHandler.focusedIndex = 0;
        }
    };

    handleSearchClearKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        const { TAB, ESCAPE } = KEYBOARD_KEYS;
        // Makes sure that the Escape and Tab key works when the search clear is focussed
        if (event.key === TAB || event.key === ESCAPE) {
            this.dropdownEventHandler.closeDropdown();
            this.clearSearch();
        }
    };

    render() {
        const {
            theme,
            dataStore,
            accessibilityStore,
            filterStore,
            filter,
            // withFilter HoC props
            selectedCount,
            total,
            filterDropdownText,
            isRequired = false,
            disclaimers,
            enableSearch,
            translationStore,
        } = this.props;
        const {
            isOpen,
            isHeaderFocused,
            toggleOpen,
        } = this.dropdownEventHandler;
        const isDropdown = filter.ui === 'dropdown';
        const dropdownId = filter.label.toLowerCase().replace(/\s/g, '-');

        const dropdownElement = dataStore.shadowRoot.getElementById(
            'fundcenter-dropdown'
        );

        return (
            <FilterDropdownWrapper {...theme.FilterDropdownWrapper}>
                <FilterDropdownInnerWrapper
                    ref={this.setAnchorNodeRef}
                    {...theme.FilterDropdownInnerWrapper}
                >
                    <FilterDropdownHeader {...theme.FilterDropdownHeader}>
                        <FilterDropdownLabelWrapper
                            id={dropdownId}
                            htmlFor={dropdownId}
                            {...theme.FilterDropdownLabelWrapper}
                        >
                            <Translate>{filter.label}</Translate>
                            <InlineDisclaimers
                                accessibilityContext={{
                                    key: `More information about the {label} filter`,
                                    placeholders: {
                                        label: filter.label
                                    }
                                }}
                                conditionalDisclaimers={disclaimers}
                                superscriptScope={SuperscriptScope.module}
                                forceSuperScripts={false}
                            />
                        </FilterDropdownLabelWrapper>
                        {selectedCount > 0 && (
                            <SelectedFilterInfo>
                                {filterStore.clearIndividualFilters.enabled && (
                                    <ClearIndividualFiltersButton
                                        id="clear-filters"
                                        aria-label={accessibilityStore.buttonAccessibilityValue(
                                            'ClearIndividualFiltersButton',
                                            'Clear {filterLabel} filter',
                                            {
                                                filterLabel: translationStore.translate(
                                                    filter.label
                                                ),
                                            }
                                        )}
                                        {...(filterStore.clearIndividualFilters
                                            .align === 'right' &&
                                            rightAlignStyleProps)}
                                        {...theme.ClearIndividualFiltersButton}
                                        onClick={
                                            filterStore.filtersActive
                                                ? (
                                                    event: React.ChangeEvent<
                                                        HTMLButtonElement
                                                    >
                                                ) => {
                                                    filterStore.clearFiltersOfType(
                                                        filter.property,
                                                        filter.filterSearchKey
                                                    );
                                                    if (isOpen) {
                                                        toggleOpen(event);
                                                    }
                                                }
                                                : () => undefined
                                        }
                                        data-metric-id={`filterItem-clear-${camelCase(
                                            filter.property
                                        )}`}
                                    >
                                        <Icon
                                            disableAltText={true}
                                            height="1.4em"
                                            width="1.4em"
                                            margin="0 0.25em 0 0.25em"
                                            fileName="ClearFiltersIcon"
                                            {...theme.ClearFiltersIcon}
                                        />
                                    </ClearIndividualFiltersButton>
                                )}
                                {!isDropdown && (
                                    <Badge
                                        fontSize="0.75em"
                                        data-selected={selectedCount > 0}
                                        selected={{
                                            bgColor: 'primary',
                                        }}
                                        {...theme.FilterDropdownTotalBadge}
                                    >
                                        {selectedCount}/{total}
                                    </Badge>
                                )}
                            </SelectedFilterInfo>
                        )}
                    </FilterDropdownHeader>
                    <FilterItemDropdownButton
                        id={dropdownId}
                        role="combobox"
                        aria-labelledby={dropdownId}
                        aria-expanded={isOpen}
                        aria-controls={`${dropdownId}-expanded-content`}
                        isFocused={isHeaderFocused}
                        onClick={(event: React.ChangeEvent<HTMLElement>) => {
                            toggleOpen(event);
                        }}
                        title={filterDropdownText}
                        dataSelected={isOpen || selectedCount > 0}
                        dataMetricId={`filterItemDropdown-button-${camelCase(
                            filter.property
                        )}`}
                        {...this.buttonKeyboardProps}
                        {...theme.FilterDropdownButton}
                        {...(isRequired
                            ? theme.FilterDropdownButtonRequired
                            : {})}
                    >
                        <FilterDropdownText {...theme.FilterDropdownText}>
                            {filterDropdownText}
                        </FilterDropdownText>
                        <Icon
                            disableAltText={true}
                            fileName={
                                isOpen
                                    ? 'FilterDropdownIconExpanded'
                                    : 'FilterDropdownIcon'
                            }
                            {...theme.FilterDropdownIcon}
                        />
                    </FilterItemDropdownButton>
                    {isOpen && (
                        <React.Fragment>
                            <ClickLayer
                                data-metric-id={'filterItemDropdown-clickLayer'}
                                onClick={(
                                    event: React.ChangeEvent<HTMLElement>
                                ) => {
                                    this.resetDropdownItemsAndEvents();
                                    toggleOpen(event);
                                }}
                            />
                            {createPortal(
                                <FilterDropdown
                                    ref={this.setFilterDropdownElement}
                                    {...theme.FilterDropdown}
                                    style={{
                                        ...this.filterDropdownHiddenStyle,
                                    }}
                                >
                                    {enableSearch && (
                                        <FilterSearchWithRef
                                            ref={this.filterSearchInputRef}
                                            id="filter-search"
                                            type="text"
                                            placeholder={`${translate(
                                                translationStore,
                                                'Search'
                                            )} ${translate(
                                                translationStore,
                                                filter.label
                                            )}`}
                                            value={this.searchTerm}
                                            onFocus={(_: FocusEvent) => {
                                                // When the input takes focus we need to ensure that the focussed index is reset
                                                // Otherwise the key down will refocus the last focussed item on every keypress
                                                this.dropdownEventHandler.focusedIndex = -1;
                                            }}
                                            onChange={this.searchDropdownItems}
                                            onKeyDown={this.handleSearchKeyDown}
                                            onClear={() => {
                                                this.clearSearch();
                                                this.searchResults = [];
                                                this.resetDropdownItemsAndEvents();
                                                // After clearing the input we need to refocus the input
                                                if (this.filterSearchInputRef) {
                                                    this.filterSearchInputRef.current.focus();
                                                }
                                            }}
                                            onClearKeyDown={
                                                this.handleSearchClearKeyDown
                                            }
                                            showClear={this.showClear}
                                            tabIndex={-1}
                                            {...theme.FilterDropdownSearch}
                                        />
                                    )}
                                    <FilterList
                                        role="listbox"
                                        tabIndex={-1}
                                        {...theme.FilterDropdownFilterList}
                                    >
                                        {this.mustVirtualize && (
                                            <FilterListAutosizerWrapper>
                                                <AutoSizer
                                                    children={this.autosizeFn}
                                                />
                                            </FilterListAutosizerWrapper>
                                        )}
                                        {!this.mustVirtualize &&
                                            this.dropdownItems.map(
                                                (dropdownItem, idx) =>
                                                    this.renderFilterValue(
                                                        dropdownItem,
                                                        idx
                                                    )
                                            )}
                                    </FilterList>
                                </FilterDropdown>,
                                dropdownElement
                            )}
                        </React.Fragment>
                    )}
                </FilterDropdownInnerWrapper>
                <VisuallyHiddenWrapper>
                    <span role="status" aria-live="polite">
                        {this.searchResultCount &&
                            accessibilityStore.dropdownAccessibilityValue(
                                'DropdownFilterSearchCount',
                                '{currentCount} of {totalCount} items',
                                {
                                    currentCount: this.searchResultCount,
                                    totalCount: this.dropdownItemsCount,
                                }
                            )}
                    </span>
                </VisuallyHiddenWrapper>
            </FilterDropdownWrapper>
        );
    }
}

export default withTheme(FilterItemDropdown);



// WEBPACK FOOTER //
// ./src/Components/Filters/Components/Filter/FilterItemDropdown.tsx